import { useContext, useState } from "react";
import { Table, Container, Row, Col, Button, Modal } from "react-bootstrap";
import { GlobalContext } from "../../../store/GlobalState";
import axios from 'axios';
import { apiUrl } from '../../../utilities/requestHelpers';
import { createToast } from '../../../utilities/toastHelpers';
import DeleteIcon from '../../Icons/delete.png';
import '../User/UsersPage.css';
import dayjs from 'dayjs';
import * as dayjsLocalizedFormat from 'dayjs/plugin/localizedFormat';
// import { faPlusSquare } from '@fortawesome/pro-regular-svg-icons';
// import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import UserInvitationModal from '../../UserInvitation/UserInvitationModal'

/*
 * Display list of invitations separated by tabs, Pending, Accepted, Declined, Expired
 * Actions: Send new token (Pending & Expired)
 *          Create: Modal form - name, email, is manager
 */

const UserInvitationPage = () => {
  dayjs.extend(dayjsLocalizedFormat);
  
  const { 
    isAdmin, 
    jwtToken, 
    invitations,
    removeInvitation,
    toggleToasts
  } = useContext(GlobalContext);

  const [userInvitationModalShow, setUserInvitationModalShow] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedInvitationId, setSelectedInvitationId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const statusEnum = {
    "Pending": 1,
    "Accepted": 2,
    "Expired": 4,
    "Rejected": 8
  };

  let getStatusName = (statusVal) => {
    return Object.keys(statusEnum)[Object.values(statusEnum).findIndex(v => v === statusVal)];
  };

  const handleOpenDeleteModal = (invitationId) => {
    setSelectedInvitationId(invitationId);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setSelectedInvitationId(null);
    setShowDeleteModal(false);
  };

  const handleDeleteInvitation = async (invitationId) => {
    if (isDeleting) return; // prevent double-click

    try {
      setIsDeleting(true);

      const response = await axios.post(`${apiUrl}/authentication/deleteinvitation`, null, {
        params: { invitationId },
      });

      if (response.status === 200) {
        removeInvitation(invitationId);
        toggleToasts(createToast('success', 'Invitation Deleted Successfully.'));
        handleCloseDeleteModal();
      }
    } catch (error) {
      console.warn(error);
      toggleToasts(createToast('error', 'Failed to delete invitation.'));
    } finally {
      setIsDeleting(false);
    }
  };

  const columns = [
    { heading: 'Name', field: (row) => `${row.user.firstName} ${row.user.lastName}` },
    { heading: 'Email', field: (row) => `${row.user.email}` },
    { heading: 'Date Created', field: (row) => dayjs(row.dateCreated).format('LLL') },
    { heading: 'Date Expires', field: (row) => dayjs(row.dateExpires).format('LLL') },
    { heading: 'Date Sent', field: (row) => row.dateSent === null ? '--' : dayjs(row.dateSent).format('LLL') },
    { heading: 'Status', field: (row) => getStatusName(row.status) },
    {
      heading: '', field: (row) => (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <button
            className="user-delete-button"
            title="Delete Invitation"
            onClick={() => handleOpenDeleteModal(row.id)}
          >
            <img src={DeleteIcon} alt="Delete" className="close-icon-image" />
          </button>
        </div>
      )
    }
  ];

  const tableHeading = (
    <tr>
      { columns.map((column, idx) => <th key={idx}>{column.heading}</th>) }
    </tr>
  );

  const tableData = (invitations.length > 0)
    ? (
      <Table striped bordered hover variant="dark">
        <thead>{ tableHeading }</thead>
        <tbody>{ invitations.map((row) => <tr key={row.id}>{ columns.map((column, idx) => <td key={idx}>{column.field(row)}</td>)}</tr>) }</tbody>
      </Table>)
    : (<p className="text-center">There are no invitations at this time.</p>);


  const pageBody =
    jwtToken && isAdmin === 2 ? (
      <>
        <div className="d-flex">
          <h1 className="p-2">User Invitations</h1>
          <div className="header-actions p-2 align-self-center justify-content-center">
            <Button variant="outline-primary" size="sm" onClick={(() => setUserInvitationModalShow(true))}>
              {/* <FontAwesomeIcon title="Create Invitation" size="lg" icon={faPlusSquare} />&nbsp; */}
              Create Invitation
            </Button>
          </div>
        </div>
        { tableData }
      </>
    ) : (
      <p>You are not allowed to view this page.</p>
    );

  return (
    <div className="user-invitation-page">
      <Container>
        <Row>
          <Col>
            {pageBody}
          </Col>
        </Row>
        <UserInvitationModal show={userInvitationModalShow} onHide={() => setUserInvitationModalShow(false)} />
      </Container>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={handleCloseDeleteModal}
        centered
        backdrop="static"
        className="delete-confirmation-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold text-danger">Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          <p className="fs-5 mb-2">Are you sure you want to delete this invitation?</p>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-center">
          <Button variant="secondary" onClick={handleCloseDeleteModal} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => handleDeleteInvitation(selectedInvitationId)}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Deleting...
              </>
            ) : (
              'Yes, Delete'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default UserInvitationPage;
